/**
 * Sanitizer for operator-written Markdown rendered with `rehype-raw`. Runs after it, so raw HTML has
 * already become elements: anything off the allowlist is dropped with its children, every attribute
 * is removed, and links keep only an `href` with a harmless scheme.
 *
 * `rehype-raw` stays because the blog editor's underline toggle writes `<u>` into the Markdown.
 * Without this pass an operator could store `<img onerror>` and run script for every visitor.
 */
type HastNode = {
    type: string;
    tagName?: string;
    properties?: Record<string, unknown>;
    children?: HastNode[];
};

const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'hr',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'del',
    'code',
    'pre',
    'a',
    'ul',
    'ol',
    'li',
    'blockquote'
]);

// Absolute http(s)/mailto/tel, or relative (no scheme at all). `javascript:` and friends fail both.
const SAFE_HREF = /^(?:https?:|mailto:|tel:|[^:]*$)/i;

function sanitizeChildren(node: HastNode): void {
    if (!node.children) {
        return;
    }
    node.children = node.children.filter((child) => {
        if (child.type === 'comment' || child.type === 'raw') {
            return false;
        }
        if (child.type !== 'element') {
            return true;
        }
        if (!child.tagName || !ALLOWED_TAGS.has(child.tagName)) {
            return false;
        }
        const href = child.properties?.href;
        child.properties =
            child.tagName === 'a' && typeof href === 'string' && SAFE_HREF.test(href.trim()) ? { href } : {};
        sanitizeChildren(child);
        return true;
    });
}

export default function rehypeAllowlist() {
    return (tree: HastNode): void => sanitizeChildren(tree);
}
